import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { qk } from "@/lib/query-client";
import type {
  ApiEnvelope,
  ChatMessage,
  ChatUsageResponse,
  ClearChatHistoryResponse,
  SendChatMessageResponse,
} from "@/lib/api-types";
import { isRetryableAiError } from "@/lib/ai-errors";

/** GET /chat/usage -> current tutor/test windows + subscription AI budget. */
export function useChatUsage(enabled = true) {
  return useQuery({
    queryKey: qk.chat.usage(),
    enabled,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ChatUsageResponse>>("/chat/usage");
      const data = unwrap(res.data);
      if (!data) throw new Error("Empty response from chat usage handler");
      return data;
    },
  });
}

/**
 * GET /chat/history/{chapter_id} -> data: ChatMessage[] (oldest first, up to 200)
 */
export function useChatHistory(chapterId: string | undefined) {
  return useQuery({
    queryKey: qk.chat.history(chapterId ?? ""),
    enabled: !!chapterId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const res = await api.get<ApiEnvelope<ChatMessage[]>>(
        `/chat/history/${encodeURIComponent(chapterId!)}`,
      );
      return unwrap(res.data) ?? [];
    },
  });
}

/**
 * POST /chat/message body { chapter_id, content }
 * Returns both messages + quota usage. We optimistically insert the
 * user message and replace with the server's two-message return on
 * success.
 */
export function useSendChatMessage(chapterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const res = await api.post<ApiEnvelope<SendChatMessageResponse>>(
        "/chat/message",
        { chapter_id: chapterId, content },
      );
      const data = unwrap(res.data);
      if (!data) throw new Error("Empty response from chat handler");
      return data;
    },
    onMutate: async (content) => {
      await qc.cancelQueries({ queryKey: qk.chat.history(chapterId) });
      const previous = qc.getQueryData<ChatMessage[]>(qk.chat.history(chapterId)) ?? [];
      const existingRetryBubble = previous.some(
        (message) =>
          message.message_id.startsWith("optimistic-") &&
          message.role === "user" &&
          message.content === content,
      );
      if (existingRetryBubble) return { previous };
      const optimistic: ChatMessage = {
        message_id: `optimistic-${Date.now()}`,
        role: "user",
        content,
        timestamp_iso: new Date().toISOString(),
        chapter_id: chapterId,
      };
      qc.setQueryData<ChatMessage[]>(qk.chat.history(chapterId), [...previous, optimistic]);
      return { previous };
    },
    onError: (err, _content, ctx) => {
      // Gemini failures persist the server-side user turn. Keep its optimistic
      // counterpart visible beside the inline Retry affordance.
      if (isRetryableAiError(err)) return;
      if (ctx?.previous) qc.setQueryData(qk.chat.history(chapterId), ctx.previous);
    },
    onSuccess: (data) => {
      // Replace optimistic insert with the real pair returned by the server.
      qc.setQueryData<ChatMessage[]>(qk.chat.history(chapterId), (existing) => {
        const withoutOptimistic = (existing ?? []).filter(
          (m) => !m.message_id.startsWith("optimistic-"),
        );
        return [...withoutOptimistic, data.user_message, data.assistant_message];
      });
      qc.setQueryData<ChatUsageResponse>(qk.chat.usage(), (existing) => {
        if (!existing) return existing;
        const { budget, ...tutor } = data.quota;
        return { ...existing, tutor, budget };
      });
    },
  });
}

/**
 * DELETE /chat/history/{chapter_id} -> data: { deleted: number }
 */
export function useClearChatHistory(chapterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.delete<ApiEnvelope<ClearChatHistoryResponse>>(
        `/chat/history/${encodeURIComponent(chapterId)}`,
      );
      return unwrap(res.data);
    },
    onSuccess: () => {
      qc.setQueryData<ChatMessage[]>(qk.chat.history(chapterId), []);
    },
  });
}
